import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

// How long to let a freshly-triggered registration check settle before deciding
// no update exists. The service worker fetches and compares assets in that window.
const UPDATE_CHECK_SETTLE_MS = 2000;

interface ServiceWorkerValue {
  /** A new version is downloaded and waiting to take over. */
  needRefresh: boolean;
  /** Activate the waiting worker and reload onto the new version. */
  applyUpdate: () => Promise<void>;
  /**
   * Ask every registration to re-check for a new version. Resolves true if one
   * is now waiting. Used by the manual "Check for Updates" control.
   */
  checkForUpdate: () => Promise<boolean>;
}

const ServiceWorkerContext = createContext<ServiceWorkerValue | null>(null);

/**
 * Owns the app's single service-worker registration.
 *
 * This must sit at the root rather than on any one page. It previously lived in
 * Settings, which meant a waiting update was only ever noticed while that page
 * was mounted — everywhere else the app kept serving the cached version until
 * the user force-quit it. That is invisible on desktop, where a reload is one
 * keystroke, and stranding on an iOS home-screen PWA, which has no reload
 * button at all.
 */
export function ServiceWorkerProvider({ children }: { children: ReactNode }) {
  const [registrations, setRegistrations] = useState<ServiceWorkerRegistration[]>([]);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (registration) {
        setRegistrations((prev) => (prev.includes(registration) ? prev : [...prev, registration]));
        // Catch an update that shipped while the app was closed.
        registration.update();
      }
    },
  });

  // Read inside async callbacks: `needRefresh` captured in a closure is the value
  // from that render, and the whole point of checkForUpdate is to observe a change
  // that lands *during* its await.
  const needRefreshRef = useRef(needRefresh);
  needRefreshRef.current = needRefresh;

  const applyUpdate = useCallback(async () => {
    // Reloads the page, so nothing after this runs.
    await updateServiceWorker(true);
  }, [updateServiceWorker]);

  const checkForUpdate = useCallback(async () => {
    if (needRefreshRef.current) return true;

    const known = registrations.length
      ? registrations
      : ((await navigator.serviceWorker?.getRegistrations()) ?? []);
    await Promise.all(known.map((reg) => reg.update()));

    // `needRefresh` flips via the hook's own state, so give the worker a moment
    // to install before reporting back — and read it from the ref, not the closure.
    await new Promise((resolve) => setTimeout(resolve, UPDATE_CHECK_SETTLE_MS));
    return needRefreshRef.current;
  }, [registrations]);

  const value = useMemo(
    () => ({ needRefresh, applyUpdate, checkForUpdate }),
    [needRefresh, applyUpdate, checkForUpdate]
  );

  return <ServiceWorkerContext.Provider value={value}>{children}</ServiceWorkerContext.Provider>;
}

export function useServiceWorker(): ServiceWorkerValue {
  const ctx = useContext(ServiceWorkerContext);
  if (!ctx) throw new Error('useServiceWorker must be used within a ServiceWorkerProvider');
  return ctx;
}
